import {
  useEffect,
  useState
} from 'react';

const SUPABASE_URL =
  process.env.REACT_APP_SUPABASE_URL;

const SUPABASE_KEY =
  process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;


function hasMeaningfulRichText(
  value
) {
  return String(value || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .length > 0;
}


function instructorName(
  row
) {
  return (
    row?.instructors?.name ||
    row?.instructors?.email ||
    'Instructor'
  );
}


export default function StudentInstructorFeedback({
  submissionType,
  submissionId,
  enrolmentId = null,
  effectiveFeedback,
  gradingMode
}) {
  const [
    feedbackRows,
    setFeedbackRows
  ] = useState(null);

  const [
    gradeOverride,
    setGradeOverride
  ] = useState(null);


  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (
        !submissionType ||
        !submissionId
      ) {
        setFeedbackRows([]);
        return;
      }

      try {
        const response =
          await fetch(
            SUPABASE_URL +
              '/rest/v1/instructor_submission_feedback' +
              '?submission_type=eq.' +
              encodeURIComponent(
                submissionType
              ) +
              '&submission_id=eq.' +
              encodeURIComponent(
                submissionId
              ) +
              '&submitted=eq.true' +
              '&select=id,comment,submitted_at,instructors(id,name,email)' +
              '&order=submitted_at.asc',
            {
              headers: {
                apikey:
                  SUPABASE_KEY,

                Authorization:
                  'Bearer ' +
                  SUPABASE_KEY
              }
            }
          );

        if (!response.ok) {
          throw new Error(
            'Could not load instructor comments (' +
              response.status +
              ')'
          );
        }

        const rows =
          await response.json();

        if (!cancelled) {
          setFeedbackRows(
            Array.isArray(rows)
              ? rows
              : []
          );
        }

      } catch (error) {
        console.error(
          'Loading student-visible instructor feedback failed:',
          error
        );

        /*
         * Preserve historical / legacy feedback
         * rather than hiding it if the new table
         * cannot be read.
         */
        if (!cancelled) {
          setFeedbackRows([]);
        }
      }
    }

    load();

    return () => {
      cancelled = true;
    };
  }, [
    submissionType,
    submissionId
  ]);


  useEffect(() => {
    let cancelled = false;

    async function loadGradeOverride() {
      if (
        submissionType !== 'assignment' ||
        !submissionId ||
        !enrolmentId
      ) {
        setGradeOverride(null);
        return;
      }

      try {
        const response =
          await fetch(
            SUPABASE_URL +
              '/rest/v1/assignment_group_grade_overrides' +
              '?assignment_submission_id=eq.' +
              encodeURIComponent(
                submissionId
              ) +
              '&enrolment_id=eq.' +
              encodeURIComponent(
                enrolmentId
              ) +
              '&select=score' +
              '&limit=1',
            {
              headers: {
                apikey:
                  SUPABASE_KEY,

                Authorization:
                  'Bearer ' +
                  SUPABASE_KEY
              }
            }
          );

        if (!response.ok) {
          throw new Error(
            'Could not load individual grade override (' +
              response.status +
              ')'
          );
        }

        const rows =
          await response.json();

        if (!cancelled) {
          setGradeOverride(
            Array.isArray(rows)
              ? rows[0] || null
              : null
          );
        }

      } catch (error) {
        console.error(
          'Loading individual group-grade override failed:',
          error
        );

        if (!cancelled) {
          setGradeOverride(null);
        }
      }
    }

    loadGradeOverride();

    return () => {
      cancelled = true;
    };
  }, [
    submissionType,
    submissionId,
    enrolmentId
  ]);


  const submittedComments =
    (feedbackRows || [])
      .filter(
        row =>
          hasMeaningfulRichText(
            row.comment
          )
      );


  /*
   * Old feedback may predate
   * instructor_submission_feedback.
   *
   * Only use the legacy comment when there
   * are no submitted per-instructor comments,
   * otherwise it would duplicate the effective
   * instructor's comment.
   */
  const showLegacyComment =
    feedbackRows !== null &&
    submittedComments.length === 0 &&
    hasMeaningfulRichText(
      effectiveFeedback?.comment
    );


  const displayedScore =
    gradeOverride?.score !==
      null &&
    gradeOverride?.score !==
      undefined
      ? gradeOverride.score
      : effectiveFeedback?.score;


  return (
    <>
      <div style={styles.heading}>
        Instructor feedback
      </div>

      {submittedComments.map(
        row => (
          <div
            key={row.id}
            style={styles.commentCard}
          >
            <div
              style={
                styles.instructorName
              }
            >
              {instructorName(row)}
            </div>

            <div
              style={
                styles.commentText
              }
              dangerouslySetInnerHTML={{
                __html:
                  row.comment || ''
              }}
            />
          </div>
        )
      )}

      {showLegacyComment && (
        <div
          style={styles.commentCard}
        >
          <div
            style={
              styles.instructorName
            }
          >
            Instructor
          </div>

          <div
            style={
              styles.commentText
            }
            dangerouslySetInnerHTML={{
              __html:
                effectiveFeedback
                  ?.comment ||
                ''
            }}
          />
        </div>
      )}

      {gradingMode ===
        'traditional' &&
        displayedScore !==
          null &&
        displayedScore !==
          undefined && (
          <div
            style={
              styles.officialOutcome
            }
          >
            Official score:{' '}
            <strong>
              {Number(
                displayedScore
              )}%
            </strong>
          </div>
        )}

      {effectiveFeedback
        ?.completion_status && (
        <div
          style={
            styles.officialOutcome
          }
        >
          Official status:{' '}
          <strong>
            {
              effectiveFeedback
                .completion_status
            }
          </strong>
        </div>
      )}
    </>
  );
}


const styles = {
  heading: {
    fontWeight: 800,
    fontSize: '13px',
    marginBottom: '10px'
  },

  commentCard: {
    padding: '10px 0',
    borderBottom:
      '1px solid rgba(0,0,0,0.08)'
  },

  instructorName: {
    fontSize: '12px',
    fontWeight: 800,
    marginBottom: '5px',
    color: '#215b7d'
  },

  commentText: {
    fontSize: '14px',
    lineHeight: 1.55
  },

  officialOutcome: {
    marginTop: '10px',
    fontSize: '13px'
  }
};
